import { ArrowUpRight, Check, ChevronDown, Copy, Download, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranscriptCopy } from "@/hooks/conversation/useTranscriptCopy";
import { normalizeTranscript } from "@/lib/conversation/transcript";
import { cn } from "@/lib/utils";
import { Conversation } from "@/types/conversation/conversation";
import { TranscriptLine } from "@/types/conversation/transcript";

interface TranscriptCopyButtonProps {
  /** A full Conversation (detail surfaces + inbox rows). Enables on-demand fetch. */
  conversation?: Conversation;
  /** Already-normalized transcript lines, when the surface holds them (detail). */
  transcript?: TranscriptLine[];
  /** Direct fields for non-Conversation surfaces (e.g. Insights). */
  genieName?: string;
  dateRaw?: string | null;
  durationSeconds?: number | null;
  rawTranscript?: unknown;
  /** A meeting rather than a customer conversation; derived from `conversation.type` when omitted. */
  isMeeting?: boolean;
  /** Icon-first, label-less treatment for dense list rows. */
  compact?: boolean;
  className?: string;
}

/**
 * Split button for grabbing a transcript: the main face copies it in one click;
 * the caret opens a short menu to download it as .txt or drop it straight into
 * ChatGPT, Claude, Gemini, or Copilot. Lives beside the analysis tabs, in the transcript card header, and
 * (compact) on every conversation list row. guidelines/design.md §7 "brand-tinted soft
 * button", mirroring ConversationShareButton's gradient-on-white treatment.
 */
export function TranscriptCopyButton({
  conversation,
  transcript,
  genieName,
  dateRaw,
  durationSeconds,
  rawTranscript,
  isMeeting,
  compact = false,
  className,
}: TranscriptCopyButtonProps) {
  const { copy, download, sendToAi, copied, isPreparing, targets } = useTranscriptCopy({
    conversation,
    transcript,
    genieName,
    dateRaw,
    durationSeconds,
    rawTranscript,
    isMeeting,
  });

  // Hide only when we're certain there's nothing to copy and no way to fetch it.
  const loadedLines = transcript?.length ? transcript : normalizeTranscript(rawTranscript);
  const messageCount = loadedLines.length || conversation?.metadata?.message_count;
  const mayHaveTranscript = loadedLines.length > 0 || (!!conversation && messageCount !== 0);
  if (!mayHaveTranscript) return null;

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const mainIcon = isPreparing ? (
    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#4E9CFF]" />
  ) : copied ? (
    <Check className="w-3.5 h-3.5 text-[#4E9CFF]" />
  ) : (
    <Copy className="w-3.5 h-3.5 text-[#FF4DB8]" />
  );

  return (
    <div
      onClick={stop}
      className={cn(
        "inline-flex items-stretch rounded-lg border bg-white overflow-hidden",
        className,
      )}
      style={{ borderColor: "rgba(78,156,255,0.3)" }}
    >
      <button
        type="button"
        onClick={copy}
        disabled={isPreparing}
        className={cn(
          "inline-flex items-center gap-1.5 font-semibold transition-colors hover:bg-gradient-to-r hover:from-[#4E9CFF]/8 hover:to-[#FF4DB8]/8 disabled:opacity-60",
          compact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-xs sm:text-sm",
        )}
        title="Copy the full transcript"
      >
        {mainIcon}
        {compact ? (
          <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent whitespace-nowrap">
            {copied ? "Copied" : "Transcript"}
          </span>
        ) : (
          <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent whitespace-nowrap">
            {isPreparing ? "Preparing…" : copied ? "Copied" : "Copy transcript"}
          </span>
        )}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="px-1.5 flex items-center border-l hover:bg-gradient-to-r hover:from-[#4E9CFF]/8 hover:to-[#FF4DB8]/8 transition-colors"
            style={{ borderColor: "rgba(78,156,255,0.2)" }}
            title="Download the transcript, or send it to ChatGPT, Claude, Gemini or Copilot"
          >
            <ChevronDown className="w-3.5 h-3.5 text-[#4E9CFF]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" onClick={stop}>
          <div className="px-2 py-1.5">
            <p className="text-xs font-semibold text-[#222B45]">Copy &amp; analyze</p>
            <p className="text-[11px] text-gray-500 leading-snug">
              Drop this transcript into ChatGPT, Claude, Gemini or Copilot to dig deeper.
            </p>
          </div>
          <DropdownMenuItem onClick={copy} className="gap-2 cursor-pointer">
            <Copy className="w-4 h-4 text-[#4E9CFF]" />
            <span>Copy transcript</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={download} className="gap-2 cursor-pointer">
            <Download className="w-4 h-4 text-[#4E9CFF]" />
            <span>Download .txt</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {targets.map((target) => (
            <DropdownMenuItem
              key={target.id}
              onClick={() => sendToAi(target)}
              className="gap-2 cursor-pointer"
            >
              <span className="flex-1">Send to {target.label}</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-gray-400" />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
